import React, { useContext, useRef, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import ReactModal from 'react-modal-resizable-draggable';
import { MobXProviderContext, observer } from 'mobx-react';
import { register } from '../../../i18n';
import toolbarService from '../../services/ToolbarService';
import RichTextEditor, { BASIC_CKEDITOR_TOOLBAR, MATH_TOOLBAR } from '../RichTextEditor';
import { LessonMode } from '../../../Constants';
import useAccessibilityClick from '../../../hooks/useAccessibilityClick';
import '../../../css/NotepadTool.scss';
import '../../../css/Accessibility.scss';
import useReactModalResizeableDraggable from '../../../hooks/useReactModalResizeableDraggable';
import lessonService from '../../services/LessonService';
import DialogPositionService from '../../services/DialogPositionService';

const t = register('ToolNames');
const t2 = register('GlobalQuestionLabels');
const t3 = register('AriaLabels');

const ContentNotepad = observer(() => {
  const {
    toolbarManager,
    userManager,
    lessonManager
  } = useContext(MobXProviderContext);

  // Focus on the modal when the content notepad dialog opens, then focus on editor when ready
  useEffect(() => {
    // Focus on the flexible-modal first
    setTimeout(() => {
      const flexibleModal = document.querySelector('.contentNotepadTool .flexible-modal');
      if (flexibleModal) {
        flexibleModal.setAttribute('tabindex', '0');
        flexibleModal.focus();
      }
    }, 100);
  }, []);

  const handleEditorReady = (editor) => {
    // Focus on the editor after it's ready
    setTimeout(() => {
      if (editor?.editing?.view?.focus) {
        editor.editing.view.focus();
      }
    }, 150);
  };

  // Announce when content notepad dialog opens
  useEffect(() => {
    const announcementElement = document.getElementById('content-notepad-dialog-announcement');
    if (announcementElement) {
      announcementElement.textContent = 'Content Notepad dialog opened';
    }
  }, []);

  const notePadSettings = toolbarService.getButtonSettings('ContentNotepad');

  const isReadOnly = (userManager.isStudent);
  const canTransact = (userManager.hasTeacherPermission);

  const toolbar = BASIC_CKEDITOR_TOOLBAR;
  const mathToolbar = MATH_TOOLBAR;
  const [enteredText, setEnteredText] = useState((lessonManager.contentNoteText) ? lessonManager.contentNoteText : '');
  const saveNoteRef = useRef();

  useEffect(() => {
    if (canTransact && lessonManager.lessonContentItem && lessonManager.lessonContentItem.entityId) {
      // Use async/await to ensure the API call completes
      const fetchNote = async () => {
        await lessonService.fetchContentNote(lessonManager.lessonContentItem.entityId);
        setEnteredText(lessonManager.contentNoteText || '');
      };
      fetchNote();
    }
  }, [lessonManager.lessonContentItem]);

  useAccessibilityClick(saveNoteRef, (e) => {
    if (lessonManager.playerMode !== LessonMode.ACTIVITY_MODE && canTransact) {
      // If the text that was entered is the same as the text that is currently saved there is no need to save again.
      if (enteredText !== lessonManager.contentNoteText) {
        lessonService.saveContentNote(enteredText);
      }
    }
    // Remove focus from the button after click to prevent it from staying expanded
    if (saveNoteRef.current) {
      saveNoteRef.current.blur();
    }
  });

  const closeNoteRef = useRef();
  useAccessibilityClick(closeNoteRef, (e) => {
    toolbarManager.setIsContentNotepadOpen(false);
  });

  useReactModalResizeableDraggable('contentNotepadTool');

  return (
    createPortal(
      <div className='notepadTool contentNotepadTool'>
        <ReactModal
          disableKeystroke
          initHeight={300}
          initWidth={280}
          isOpen={true}
          left={325}
          onRequestClose={() => toolbarManager.setIsContentNotepadOpen(false)}
          role='dialog'
          top={DialogPositionService.getDefaultTopPosition()}
          aria-labelledby="content-notepad-dialog-title">
          <div className='note-body'>
            <div className='tool-header'>
              <h2 className='title-label basic' id='content-notepad-dialog-title'>{t('contentNotepadLabel')}</h2>
            </div>
            <div aria-label={t3('enterNotes')} className='editor-wrapper'>
              <div className='cke_topspace'>
                <RichTextEditor
                  data={enteredText}
                  onChange={(data) => setEnteredText(data)}
                  onReady={handleEditorReady}
                  readOnly={isReadOnly}
                  tabIndex={-1}
                  toolbar={(notePadSettings.ckeditor === 'math') ? mathToolbar : toolbar}
                  skipRole={true} />
              </div>
            </div>
            <div className='footer-buttons'>
              {(!isReadOnly) ?
                <button ref={saveNoteRef} className='submit-response primary'>{t2('save')}</button> : ''}
            </div>
          </div>
        </ReactModal>
      </div>, document.getElementById('the-body'))
  );
});

export default ContentNotepad;
